import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useFlight } from '../contexts/FlightContext';
import { useAuth } from '../contexts/AuthContext';
import { ScannedPassenger, UserRole } from '../types';
import { CheckIcon, PencilIcon, CameraIcon, XCircleIcon } from '../components/Icons';

const SunIcon = (props: React.SVGProps<SVGSVGElement>) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v2.25m6.364.386-1.591 1.591M21 12h-2.25m-.386 6.364-1.591-1.591M12 18.75V21m-4.773-4.227-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0Z" />
    </svg>
);

// --- Barcode Scanner Component ---
// Note: This is included in the same file to adhere to project constraints.
interface BarcodeScannerProps {
  onScanSuccess: (value: string) => void;
  onClose: () => void;
  onManualEntry: (seatNumber: string) => void;
}

const BarcodeScanner: React.FC<BarcodeScannerProps> = ({ onScanSuccess, onClose, onManualEntry }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [error, setError] = useState<string | null>(null);
  const animationFrameId = useRef<number | null>(null);
  const [isFlashlightOn, setIsFlashlightOn] = useState(false);
  const [hasFlashlight, setHasFlashlight] = useState(false);
  const streamTrack = useRef<MediaStreamTrack | null>(null);
  const [manualSeat, setManualSeat] = useState('');
  const [scanStatus, setScanStatus] = useState<'searching' | 'success'>('searching');

  const toggleFlashlight = useCallback(() => {
    if (streamTrack.current && hasFlashlight) {
        const nextState = !isFlashlightOn;
        streamTrack.current.applyConstraints({
            advanced: [{ torch: nextState } as any]
        }).then(() => {
            setIsFlashlightOn(nextState);
        }).catch(e => {
            console.error('Failed to toggle flashlight', e);
            setError('Could not control flashlight.');
        });
    }
  }, [hasFlashlight, isFlashlightOn]);

  useEffect(() => {
    // Check for secure context (HTTPS) which is required for camera access
    if (!window.isSecureContext) {
      setError('Camera access requires a secure connection (HTTPS).');
      return;
    }
    
    // Check for browser support
    if (!('BarcodeDetector' in window)) {
      setError('Barcode scanning is not supported by this browser.');
      return;
    }

    const barcodeDetector = new (window as any).BarcodeDetector({ formats: ['pdf417'] });
    let stream: MediaStream | null = null;

    const startScan = async () => {
      try {
        // Request a higher resolution video stream for better and faster barcode recognition.
        stream = await navigator.mediaDevices.getUserMedia({ 
            video: { 
                facingMode: 'environment',
                width: { ideal: 1280 },
                height: { ideal: 720 }
            } 
        });
        if (stream.getVideoTracks().length > 0) {
            streamTrack.current = stream.getVideoTracks()[0];
            const capabilities = streamTrack.current.getCapabilities();
            if ((capabilities as any).torch) {
                setHasFlashlight(true);
            }
        }
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
          detectBarcode();
        }
      } catch (err) {
        console.error('Error accessing camera:', err);
        setError('Could not access camera. Please check permissions.');
      }
    };

    const detectBarcode = () => {
      if (!videoRef.current || videoRef.current.paused || videoRef.current.ended) {
        return;
      }
      barcodeDetector.detect(videoRef.current)
        .then((barcodes: any[]) => {
          if (barcodes && barcodes.length > 0 && scanStatus !== 'success') {
            if (animationFrameId.current) {
                cancelAnimationFrame(animationFrameId.current);
            }
            setScanStatus('success');
            navigator.vibrate?.(200); // Vibrate on success
            setTimeout(() => {
                onScanSuccess(barcodes[0].rawValue);
            }, 400); // Give time for success animation
          } else if (scanStatus === 'searching') {
            animationFrameId.current = requestAnimationFrame(detectBarcode);
          }
        })
        .catch((err: any) => {
          console.error('Barcode detection failed:', err);
          // Don't set a permanent error, just log it, to allow retries
        });
    };

    startScan();

    return () => {
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
      }
      stream?.getTracks().forEach(track => track.stop());
    };
  }, [onScanSuccess, scanStatus]);

  return (
    <div className="fixed inset-0 bg-black z-50 animate-slide-up-fade-in">
      <video ref={videoRef} className="absolute top-0 left-0 w-full h-full object-cover" playsInline />
      
      {/* Centered content: instruction and scanner overlay */}
      <div className="relative z-10 w-full h-full flex flex-col items-center justify-center p-4 pb-32"> {/* Added pb-32 to avoid overlap with bottom bar */}
          <p className="text-white text-lg text-center font-semibold bg-black/60 px-4 py-2 rounded-lg mb-4">
              {error ? error : 'Position the barcode inside the frame'}
          </p>
          <div 
              className="relative overflow-hidden scanner-overlay scanner-frame animate-scanner-pulse w-full max-w-md aspect-square md:aspect-[16/9] rounded-2xl flex items-end justify-center"
          >
            <div className={`equalizer-container ${scanStatus}`}>
              {Array.from({ length: 15 }).map((_, i) => (
                  <div key={i} className="equalizer-bar"></div>
              ))}
            </div>
          </div>
      </div>

      {/* Fixed bottom bar for controls */}
      <div className="absolute bottom-0 left-0 right-0 z-20 p-4 bg-black/70 backdrop-blur-sm border-t border-slate-700">
        <div className="max-w-md mx-auto">
          <div className="flex items-stretch gap-2">
              <input
                  type="text"
                  value={manualSeat}
                  onChange={(e) => setManualSeat(e.target.value.toUpperCase())}
                  placeholder="Seat No. (e.g., 24A)"
                  className="w-full bg-slate-800 text-white border border-slate-600 rounded-md py-3 px-4 text-lg"
                  aria-label="Enter Seat Number Manually"
              />
              <button
                  onClick={() => onManualEntry(manualSeat)}
                  disabled={!manualSeat.trim()}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-5 rounded-md disabled:bg-slate-500 disabled:cursor-not-allowed"
              >
                  Add
              </button>
              {hasFlashlight && (
                  <button 
                      onClick={toggleFlashlight} 
                      className={`p-3 rounded-md transition-colors ${isFlashlightOn ? 'bg-amber-500' : 'bg-slate-600 hover:bg-slate-500'}`}
                      aria-label="Toggle Flashlight"
                  >
                      <SunIcon className="w-7 h-7 text-white" />
                  </button>
              )}
          </div>
          <p className="text-center text-xs text-slate-400 mt-2">If scan fails, enter seat number manually and press Add.</p>
        </div>
      </div>

      {/* Close button remains in the corner */}
      <button onClick={onClose} className="absolute top-5 right-5 z-20 text-white bg-black/60 rounded-full p-2 hover:bg-black/80 transition-colors">
          <XCircleIcon className="w-9 h-9" />
      </button>
    </div>
  );
};


const SecurityScreen: React.FC = () => {
  const { selectedFlightData: flightData, addScannedPassenger, showNotification } = useFlight();
  const { securityScanPercentage } = useAuth();
  
  const [isScannerOpen, setIsScannerOpen] = useState(false);
  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    seatNumber: string;
    name: string;
    note: string;
  }>({
    isOpen: false,
    seatNumber: '',
    name: '',
    note: '',
  });

  if (!flightData) return null;
  
  const { totalPassengers, totalPassengersSource } = useMemo(() => {
    const finalTotalStr = flightData.counterDistribution.finalFigures.totalPassengers;
    const finalTotal = parseInt(finalTotalStr, 10);
    if (!isNaN(finalTotal) && finalTotal > 0) {
      return { totalPassengers: finalTotal, totalPassengersSource: 'Final Figures' };
    }

    const bookingTotalStr = flightData.counterDistribution.booking.totalPassengers;
    const bookingTotal = parseInt(bookingTotalStr, 10);
    const total = isNaN(bookingTotal) ? 0 : bookingTotal;
    return { totalPassengers: total, totalPassengersSource: 'Booking Figures' };
  }, [flightData.counterDistribution.finalFigures, flightData.counterDistribution.booking]);


  const targetScans = useMemo(() => {
    if (totalPassengers === 0) return 0;
    return Math.ceil(totalPassengers * (securityScanPercentage / 100));
  }, [totalPassengers, securityScanPercentage]);

  const scannedPassengers = flightData.scannedPassengers || [];

   const handleOpenManualEntry = useCallback(() => {
    setModalState({
        isOpen: true,
        seatNumber: '',
        name: '',
        note: '',
    });
  }, []);

  const handleManualEntryFromScanner = useCallback((seatNumber: string) => {
    setIsScannerOpen(false);

    if (!seatNumber.trim()) {
        showNotification("Seat number cannot be empty.", "error");
        return;
    }

    const finalSeatNumber = seatNumber.trim().toUpperCase();

    // Check for duplicates
    if (scannedPassengers.some(p => p.seatNumber === finalSeatNumber)) {
        showNotification(`Seat ${finalSeatNumber} has already been scanned.`, 'error');
        return;
    }

    setModalState({
        isOpen: true,
        seatNumber: finalSeatNumber,
        name: '', // Name is unknown for manual entry from scanner view
        note: 'Manually entered',
    });
  }, [scannedPassengers, showNotification]);

  const handleScanSuccess = useCallback((rawValue: string) => {
    setIsScannerOpen(false);

    // IATA format: M1[Name(20 chars)][PNR(7 chars)]...
    // New, more reliable regex for name: captures the 20 chars after M1.
    const nameMatch = rawValue.match(/^M1(.{20})/);

    // More flexible regex for seat number to handle variations.
    // Looks for 2 or 3 digits followed by a letter. We take the last match
    // as seat numbers are typically at the end of the string.
    const seatMatches = Array.from(rawValue.matchAll(/(\d{2,3})([A-Z])/g));
    const seatMatch = seatMatches.length > 0 ? seatMatches[seatMatches.length - 1] : null;

    if (!seatMatch) {
      showNotification("Could not find a valid seat number in the barcode.", "error");
      handleOpenManualEntry(); // Open manual entry as a fallback
      return;
    }

    // seatMatch[1] is the row number string (e.g., "18" or "018"), seatMatch[2] is the seat letter (e.g., "F")
    const seatNumber = `${parseInt(seatMatch[1], 10)}${seatMatch[2]}`;

    let passengerName = 'N/A';
    if (nameMatch && nameMatch[1]) {
        // Raw name can be "SURNAME/FIRSTNAME MSTR"
        const rawNameField = nameMatch[1].trim();
        
        if (rawNameField.includes('/')) {
            let [surname, firstNameWithTitle] = rawNameField.split('/');
            surname = surname.trim();
            firstNameWithTitle = firstNameWithTitle.trim();
            
            // Remove common titles (MR, MRS, MS, MSTR, CHD, INF) from the first name part
            const firstName = firstNameWithTitle.replace(/\s*(MR|MRS|MS|MSTR|CHD|INF)$/, '').trim();
            passengerName = `${firstName} ${surname}`;
        } else {
            // Handle names without a '/' separator, also removing title
            passengerName = rawNameField.replace(/\s*(MR|MRS|MS|MSTR|CHD|INF)$/, '').trim();
        }
    }
    
    // Check for duplicates before opening modal
    if (scannedPassengers.some(p => p.seatNumber === seatNumber)) {
        showNotification(`Seat ${seatNumber} has already been scanned.`, 'error');
        return;
    }

    setModalState({
        isOpen: true,
        seatNumber: seatNumber,
        name: passengerName,
        note: 'Scanned via camera',
    });
  }, [showNotification, handleOpenManualEntry, scannedPassengers]);


  const handleModalChange = useCallback((field: 'seatNumber' | 'name' | 'note', value: string) => {
    let processedValue = value;
    if (field === 'seatNumber' || field === 'name') {
        processedValue = value.toUpperCase();
    }
    setModalState(prev => ({ ...prev, [field]: processedValue }));
  }, []);

  const savePassenger = useCallback(() => {
    const { seatNumber, name, note } = modalState;
    if (!seatNumber.trim()) {
        showNotification("Seat number is required.", 'error');
        return false;
    }
    const finalSeatNumber = seatNumber.trim();
    if (scannedPassengers.some(p => p.seatNumber === finalSeatNumber)) {
        showNotification(`Seat ${finalSeatNumber} has already been scanned.`, 'error');
        return false;
    }
    const passengerData: Omit<ScannedPassenger, 'sequence' | 'scannedAt' | 'scannedBy'> = {
        name: name.trim() || 'N/A',
        seatNumber: finalSeatNumber,
        note: note.trim(),
    };
    addScannedPassenger(passengerData);
    showNotification('Passenger added successfully.', 'success');
    return true;
  }, [modalState, addScannedPassenger, showNotification, scannedPassengers]);

  const handleConfirmPassenger = useCallback(() => {
    if (savePassenger()) {
        setModalState({ isOpen: false, seatNumber: '', name: '', note: '' });
    }
  }, [savePassenger]);

  const handleConfirmAndScanNext = useCallback(() => {
    if (savePassenger()) {
        setModalState({ isOpen: false, seatNumber: '', name: '', note: '' });
        setIsScannerOpen(true);
    }
  }, [savePassenger]);

  const handleCancelPassenger = useCallback(() => {
    setModalState({ isOpen: false, seatNumber: '', name: '', note: '' });
  }, []);
  
  const renderPassengerDetailsModal = () => {
    if (!modalState.isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-slate-800 rounded-lg shadow-2xl p-6 w-full max-w-md animate-slide-up-fade-in">
                <h2 className="text-2xl font-bold text-white mb-4">
                    Confirm Passenger Details
                </h2>
                <div className="space-y-4">
                    <div>
                        <label htmlFor="modalSeatNumber" className="block text-sm font-medium text-slate-300 mb-1">Seat Number*</label>
                        <input
                            id="modalSeatNumber"
                            type="text"
                            value={modalState.seatNumber}
                            onChange={(e) => handleModalChange('seatNumber', e.target.value)}
                            className="w-full uppercase"
                            placeholder="E.G., 12D"
                            required
                        />
                    </div>
                    <div>
                        <label htmlFor="modalNameEntry" className="block text-sm font-medium text-slate-300 mb-1">Passenger Name</label>
                        <input
                            id="modalNameEntry"
                            type="text"
                            value={modalState.name}
                            onChange={(e) => handleModalChange('name', e.target.value)}
                            className="w-full uppercase"
                            placeholder="PASSENGER NAME"
                        />
                    </div>
                    <div>
                        <label htmlFor="modalNote" className="block text-sm font-medium text-slate-300 mb-1">Add Note (Optional)</label>
                        <textarea
                            id="modalNote"
                            value={modalState.note}
                            onChange={(e) => handleModalChange('note', e.target.value)}
                            rows={2}
                            className="w-full"
                        />
                    </div>
                </div>
                <div className="flex justify-end gap-3 pt-4 mt-4 border-t border-slate-700">
                    <button onClick={handleCancelPassenger} className="bg-slate-600 hover:bg-slate-500 text-white font-bold py-2 px-4 rounded-lg">Cancel</button>
                    <button onClick={handleConfirmPassenger} className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg flex items-center"><CheckIcon className="w-5 h-5 mr-2"/>Confirm</button>
                    <button onClick={handleConfirmAndScanNext} className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-lg flex items-center"><CameraIcon className="w-5 h-5 mr-2"/>Confirm & Scan Next</button>
                </div>
            </div>
        </div>
    );
  };
  
  return (
    <div className="space-y-6">
      {isScannerOpen && <BarcodeScanner onScanSuccess={handleScanSuccess} onManualEntry={handleManualEntryFromScanner} onClose={() => setIsScannerOpen(false)} />}

      <div className="bg-slate-900/60 backdrop-blur-md border border-slate-700 rounded-xl shadow-lg p-4">
        <h2 className="text-xl font-bold text-blue-400 mb-2">ETD Sheet Security Scan</h2>
        <div className="flex justify-between items-center text-lg">
           <p className="text-slate-300">
             Target Scans: <span className="font-bold text-amber-400">{targetScans}</span> 
             <span className="text-sm text-slate-400"> ({securityScanPercentage}% of {totalPassengers} from {totalPassengersSource})</span>
           </p>
           <p className="text-slate-300">
             Completed: <span className="font-bold text-green-400">{scannedPassengers.length}</span>
           </p>
        </div>
         <div className="w-full bg-slate-700 rounded-full h-2.5 mt-2">
            <div className="bg-green-500 h-2.5 rounded-full" style={{ width: `${targetScans > 0 ? (scannedPassengers.length / targetScans) * 100 : 0}%` }}></div>
        </div>
      </div>
      
      <div className="bg-slate-900/60 backdrop-blur-md border border-slate-700 rounded-xl shadow-lg p-4">
        <div data-tour-id="security-scan-button" className="flex justify-center flex-wrap gap-4">
            <button
                onClick={() => setIsScannerOpen(true)}
                className="px-6 py-3 rounded-lg text-white font-bold flex items-center gap-3 transition-all duration-200 transform hover:scale-105 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 shadow-lg"
            >
                <CameraIcon className="w-7 h-7" />
                Scan Boarding Pass
            </button>
            <button
                onClick={handleOpenManualEntry}
                className="px-6 py-3 rounded-lg text-white font-bold flex items-center gap-3 transition-all duration-200 transform hover:scale-105 bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700 shadow-lg"
            >
                <PencilIcon className="w-6 h-6" />
                Add Manually
            </button>
        </div>
      </div>

      <div className="bg-slate-900/60 backdrop-blur-md border border-slate-700 rounded-xl shadow-lg p-4">
         <h3 className="text-lg font-bold text-blue-400 mb-4">Scanned Passengers</h3>
         <div className="max-h-96 overflow-y-auto">
             {scannedPassengers.length > 0 ? (
                <table className="min-w-full divide-y divide-slate-700">
                    <thead className="bg-slate-700/50">
                        <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-slate-300">Seq.</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-slate-300">Name</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-slate-300">Seat</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-slate-300">Note</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-slate-300">Time / By</th>
                        </tr>
                    </thead>
                    <tbody className="bg-slate-800 divide-y divide-slate-700">
                        {scannedPassengers.slice().reverse().map(p => (
                            <tr key={p.sequence}>
                                <td className="px-4 py-3 text-sm text-white">{p.sequence}</td>
                                <td className="px-4 py-3 text-sm text-white">{p.name}</td>
                                <td className="px-4 py-3 text-sm text-white font-mono">{p.seatNumber}</td>
                                <td className="px-4 py-3 text-sm text-slate-300 italic">{p.note || '-'}</td>
                                <td className="px-4 py-3 text-xs text-slate-400">
                                    {new Date(p.scannedAt).toLocaleTimeString('en-GB', {hour: '2-digit', minute:'2-digit'})} by {p.scannedBy}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
             ) : (
                <p className="text-center text-slate-400 py-4">No passengers scanned yet.</p>
             )}
         </div>
      </div>
      
      {renderPassengerDetailsModal()}
    </div>
  );
};

export default SecurityScreen;
